// apps/share-web/src/setlist-screen/text-link-redirect.ts
//
// Единый сетлист, фазы 3+4, задача D1: старые ссылки слов — `/t/<uuid>` (волна
// B) и `/setlist/w/<uuid>` (единый сетлист, фаза 1) — адресуют ОДИН и тот же
// `textShareId`. С появлением `/s/<uuid>` (гостевой/участнический экран
// сетлиста, задача 12) у части этих ссылок теперь есть более полный экран, на
// который стоит увести читателя, вместо того чтобы показывать прежнюю
// страницу «только слова».
//
// `resolveTextLink` — чистая (по сети) функция: сеть входит через два
// инжектируемых факта (`library`/`fetchSetlistKey`), а не через fetch внутри
// неё, чтобы решение «куда вести» проверялось без моков HTTP. Настоящий поход
// в сеть — в `legacyLinkRedirectPath` ниже (единственное место, откуда сюда
// приходит `fetchLibrary`, после рефакторинга D3: оба вызывающих — main.ts и
// setlist-screen/boot.ts — делили один и тот же блок до его выноса сюда).

import { fetchLibrary, type TeamLibrary } from '../library.js';

export type TextLinkResolution =
  | { kind: 'setlist'; path: string }
  | { kind: 'legacy' };

export interface ResolveTextLinkDeps {
  /**
   * Библиотека читателя — teams[] уже загруженного `GET /api/library`, или
   * `null`, когда тот ответил 401 (гость без сессии / кука истекла). `null`
   * НЕ значит «не проверять» — проверка члена команды просто не находит
   * совпадения и переходит к шагу 2 (гостевой ключ), как и полагается.
   */
  library: TeamLibrary[] | null;
  /**
   * `GET /api/setlist-keys/<id>` — 200 → `{ setlistId }`, 404/сеть → `null`.
   * Инжектируется, а не вызывается напрямую: сама функция не знает apiBase
   * или fetchImpl, это забота вызывающего (см. `fetchSetlistKey` ниже —
   * готовая реализация для прода).
   */
  fetchSetlistKey: (id: string) => Promise<{ setlistId: string } | null>;
}

/**
 * Разрешает старую ссылку слов в унифицированный экран сетлиста, если он для
 * неё есть.
 *
 * 1. Участник команды — ПЕРВЫМ: если библиотека знает сетлист с этим
 *    `textShareId` (`team.setlists[].textShareId === id`), у читателя уже
 *    есть доступ по куке — ведём на `/s/<setlistId>` БЕЗ ключа. Это обязано
 *    идти раньше шага 2: если слова/аудио закрыты гостям, `guest_key` мог
 *    оказаться отозван или никогда не существовать, и
 *    `GET /api/setlist-keys/<id>` ответил бы 404 — участник команды увидел бы
 *    «нет такой ссылки», хотя сетлист у него есть в библиотеке.
 * 2. Иначе — гостевой ключ: `GET /api/setlist-keys/<id>` (id — он же
 *    guest_key, воркер минтит их совпадающими при переносе старых ссылок,
 *    план «Решения» п.8) → 200 → `/s/<setlistId>?g=<id>`.
 * 3. Иначе (404 у шага 2, либо сеть недоступна и на шаге 1, и на шаге 2) —
 *    `legacy`: ссылка не сидит ни в одном сетлисте (заведена до фазы 3/4, или
 *    сетлист владелец не переносил) — читатель идёт на прежнюю страницу
 *    слов, офлайн-поведение не меняется (тот же путь, что и «сети нет»).
 */
export async function resolveTextLink(id: string, deps: ResolveTextLinkDeps): Promise<TextLinkResolution> {
  const memberSetlistId = deps.library
    ?.flatMap((team) => team.setlists ?? [])
    .find((s) => s.textShareId === id)?.setlistId ?? null;
  if (memberSetlistId) {
    return { kind: 'setlist', path: `/s/${encodeURIComponent(memberSetlistId)}` };
  }
  const byKey = await deps.fetchSetlistKey(id);
  if (byKey) {
    return { kind: 'setlist', path: `/s/${encodeURIComponent(byKey.setlistId)}?g=${encodeURIComponent(id)}` };
  }
  return { kind: 'legacy' };
}

/**
 * Обёртка вызывающего для двух мест main.ts/boot.ts, которым нужен готовый
 * путь редиректа, а не сырой `TextLinkResolution` — сама библиотека
 * (`fetchLibrary`) грузится ЗДЕСЬ теперь (рефакторинг после ревью D3, не
 * второй отдельный поход в обоих вызывающих): `resolveTextLink` выше
 * остаётся чистой (по сети) и по-прежнему принимает библиотеку инжектом —
 * эта функция просто закрывает её сетевой шов готовой реализацией.
 */
export async function legacyLinkRedirectPath(
  id: string,
  apiBase: string,
  fetchImpl: typeof fetch,
): Promise<string | null> {
  const libRes = await fetchLibrary(apiBase, fetchImpl);
  const resolved = await resolveTextLink(id, {
    library: libRes.ok ? libRes.teams : null,
    fetchSetlistKey: (tid) => fetchSetlistKey(tid, apiBase, fetchImpl),
  });
  return resolved.kind === 'setlist' ? resolved.path : null;
}

/**
 * Реализация `fetchSetlistKey` для прода — `GET /api/setlist-keys/<id>`
 * (`apps/share-worker/functions/api/setlist-keys/[key].ts`): 200 → тело
 * `{ ok: true, setlistId }`, 404/сеть/неразборный ответ → `null` (шаг 3 выше
 * решит, что это `legacy` — тот же честный «нет», как и у соседей fetchImpl в
 * этом приложении: сеть недоступна ровно так же, как ключ не найден, разбирать
 * разницу здесь нечем).
 */
export async function fetchSetlistKey(
  id: string,
  apiBase = '',
  fetchImpl: typeof fetch = fetch,
): Promise<{ setlistId: string } | null> {
  try {
    const res = await fetchImpl(`${apiBase}/api/setlist-keys/${encodeURIComponent(id)}`);
    if (!res.ok) return null;
    const body = await res.json() as { ok?: boolean; setlistId?: unknown };
    return body?.ok && typeof body.setlistId === 'string' ? { setlistId: body.setlistId } : null;
  } catch {
    return null;
  }
}
